import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, DocumentText, Profile, Building, UserAdd, TaskSquare, Wallet, Map1, ShieldSecurity, Buildings, ArrowDown2, ArrowUp2 } from 'iconsax-react';
import { usePermission } from '../../context/PermissionContext';

const Sidebar = () => {
  const location = useLocation();
  const { hasPermission } = usePermission();
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Close sidebar function
  const closeSidebar = () => {
    const sidebar = document.getElementById('sidebar');
    const overlay = document.getElementById('sidebar-overlay');
    if (sidebar) {
      sidebar.classList.add('translate-x-full');
      sidebar.classList.add('opacity-0');
    }
    if (overlay) {
      overlay.classList.remove('opacity-100', 'pointer-events-auto');
      overlay.classList.add('opacity-0', 'pointer-events-none');
    }
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  const toggleDropdown = (label) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const NAV_ITEMS = [
    { path: '/dashboard', label: 'داشبۆرد', icon: Home, code: 'dashboard.view' },
    { path: '/receipts', label: 'پسوڵەکان', icon: DocumentText, code: 'receipts.view' },
    { path: '/tenants', label: 'کرێچییەکان', icon: Profile, code: 'tenants.view' },
    { path: '/properties', label: 'موڵکەکان', icon: Building, code: 'properties.view' },

    { path: '/contracts', label: 'گرێبەستەکان', icon: DocumentText, code: 'contracts.view' },
    { path: '/rules', label: 'بەندەکان', icon: TaskSquare, code: 'contracts.view' },
    { path: '/expenses', label: 'خەرجییەکان', icon: Wallet, code: 'expenses.view' },
    {
      label: 'کارمەندان',
      icon: UserAdd,
      code: 'employees.view',
      children: [
        { path: '/employees', label: 'لیستی کارمەندان' },
        { path: '/payroll', label: 'لیستی موچە' }
      ]
    },
    { path: '/map', label: 'نەخشە', icon: Map1, code: 'properties.view' },
    { path: '/companies', label: 'کۆمپانیاکان', icon: Buildings, code: 'companies.view' },
  ];

  // Auto-expand dropdowns if a child is active
  useEffect(() => {
    NAV_ITEMS.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'));
        if (hasActiveChild) {
          setOpenDropdowns(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  const SidebarItem = ({ to, icon: Icon, label, children, isActiveParent, isOpen, onToggle }) => {
    if (children) {
      return (
        <div>
          <button
            onClick={onToggle}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-medium text-body-medium font-medium transition-all duration-300 ease-in-out group ${isActiveParent
              ? 'bg-neutral-50 text-primary-red dark:bg-neutral-800 dark:text-primary-red'
              : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white'
              }`}
          >
            <div className="flex items-center gap-3">
              <Icon
                size={20}
                variant={isActiveParent ? "Bold" : "Outline"}
                className={`transition-transform duration-300 ${isActiveParent ? 'scale-110' : 'group-hover:scale-110'}`}
              />
              <span>{label}</span>
            </div>
            {isOpen ? <ArrowUp2 size={16} /> : <ArrowDown2 size={16} />}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
            <ul className="pr-10 space-y-1">
              {children.map((child) => (
                <li key={child.path}>
                  <NavLink
                    to={child.path}
                    onClick={handleLinkClick}
                    end={child.path === '/employees'} // Strict match for list to allow sub-routes (like /new) to activate their own link if we wanted, but here we want exact
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-medium text-body-small font-medium transition-all ${isActive
                        ? 'bg-primary-red/10 text-primary-red dark:bg-primary-red/20'
                        : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800'
                      }`
                    }
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    return (
      <NavLink
        to={to}
        onClick={handleLinkClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-medium text-body-medium font-medium transition-all duration-300 ease-in-out group ${isActive
            ? 'bg-gradient-to-r from-primary-red/10 to-blue-500/10 text-primary-red translate-x-2 dark:from-primary-red/20 dark:to-blue-500/20'
            : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 hover:translate-x-1 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <Icon
              size={20}
              variant={isActive ? "Bold" : "Outline"}
              className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}
            />
            <span>{label}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      <aside
        id="sidebar"
        className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white/80 backdrop-blur-md border-l border-neutral-200 z-40 transform transition-all duration-300 ease-in-out translate-x-full opacity-0 lg:translate-x-0 lg:opacity-100 dark:bg-neutral-900/80 dark:border-neutral-800 flex flex-col"
      >
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {NAV_ITEMS.map((item) => {
              if (!hasPermission(item.code)) return null;

              const isDropdown = !!item.children;
              // Check if any child is active to highlight parent
              const isActiveParent = isDropdown && item.children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'));

              return (
                <li key={item.label}>
                  <SidebarItem
                    to={item.path}
                    icon={item.icon}
                    label={item.label}
                    children={item.children}
                    isActiveParent={isActiveParent}
                    isOpen={openDropdowns[item.label]}
                    onToggle={() => toggleDropdown(item.label)}
                  />
                </li>
              );
            })}
          </ul>

          {/* Settings / Users Section */}
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
            <ul className="space-y-2">
              {hasPermission('users.create') && (
                <li>
                  <SidebarItem to="/users/new" icon={UserAdd} label="زیادکردنی بەکارهێنەر" />
                </li>
              )}
              {hasPermission('roles.view') && (
                <li>
                  <SidebarItem to="/roles" icon={ShieldSecurity} label="ڕۆڵ و دەسەڵاتەکان" />
                </li>
              )}
              {hasPermission('settings.view') && (
                <li>
                  <SidebarItem to="/locations" icon={Map1} label="بەڕێوەبردنی شوێنەکان" />
                </li>
              )}
            </ul>
          </div>
        </nav>
      </aside>

      {/* Sidebar Overlay (Mobile) */}
      <div
        id="sidebar-overlay"
        className="fixed inset-0 bg-neutral-900/50 z-30 lg:hidden transition-opacity duration-300 opacity-0 pointer-events-none"
        style={{ top: '4rem' }}
        onClick={closeSidebar}
      ></div>
    </>
  );
};

export default Sidebar;
